/* eslint-disable no-param-reassign */
import { ThemeProvider } from '@oneaudi/unified-web-common';
import React, { useEffect, useRef, useState } from 'react';
import { TextButton, Headline, Icon, TextLink } from '@oneaudi/unified-web-components';
import { Logo, ResponsiveMedia } from '@oneaudi/aoa-shared-editorial-components';
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import { renderTextWithFootnotesReferences } from '@oneaudi/feature-app-utils';
import { array, arrayOf, bool, func, number, object, oneOf, shape, string } from 'prop-types';
import uuid from 'react-uuid';
import styled from 'styled-components';
import { useWindowSize } from '../../hooks/useWindowSize';
import { withScrollToElement } from '../../componentWrappers/withScrollToElement';
import WLTPComponent from '../MediaDisclaimerAndWLTP/index';
import { setAssetParams } from '../../utils/setAssetParams';
import { mediaProperty } from '../../utils/mediaProperty';

import {
  CTAsContainer,
  Container,
  ContentContainer,
  DisclaimerWrapper,
  Heading,
  Img,
  InnerWrapper,
  LogoContainer,
  MediaContainer,
  NoAnchorContainer,
  StyledAnchor,
  SubHeading,
} from './styles';

const StyledAssetWrapper = styled.div`
  display: grid;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const StyledTextLink = styled(TextLink)`
  ::before {
    display: none;
  }
  svg {
    margin: 0;
  }
`;

const StyledIcon = styled(Icon)`
  color: ${({ theme }) => (theme === 'light' && '#333333 !important') || '#e5e5e5 !important'};
`;

const ButtonWithScrollToElement = withScrollToElement(TextButton);
const TextButtonWithScrollToElement = withScrollToElement(StyledTextLink);

function Anchor({ linkEnabled, href, newWindow, onClick, children, ariaLabel, linkRef, ...rest }) {
  if (linkEnabled) {
    return (
      <StyledAnchor
        href={href}
        target={newWindow === 'New tab' ? '_blank' : '_self'}
        onClick={onClick}
        aria-label={ariaLabel}
        ref={linkRef}
        {...rest}
      >
        {children}
      </StyledAnchor>
    );
  }

  return <NoAnchorContainer>{children}</NoAnchorContainer>;
}

Anchor.displayName = 'Anchor';

const AnchorWithScroll = withScrollToElement(Anchor);

const stopPropagation = (e) => {
  const { currentTarget } = e;
  if (currentTarget.getAttribute('href')) {
    e.stopPropagation();
  }
};

export function ContentTile({
  CTA,
  disclaimers,
  heading,
  horizontalAlignment,
  horizontalAlignmentSmall,
  icon,
  logo,
  media,
  mobileBreakpointSizes,
  size,
  subHeading,
  theme,
  verticalAlignment,
  verticalAlignmentSmall,
  onClickEvent,
  index,
  layoutName,
  wltpKeys,
  isTest,
  ...ueProps
}) {
  const mediaRef = useRef(null);
  const assetContainerLabel = `${media.imageEnabled ? 'Image' : 'Video'} asset`;
  const assetRef = useRef();
  const ctaRef = useRef(null);
  const contentRef = useRef(null);
  const linkRef = useRef(null);
  const windowSize = useWindowSize();
  const [isVideoPaused, setIsVideoPaused] = useState(true);

  useEffect(() => {
    if (mediaRef && mediaRef.current) {
      const video = mediaRef.current.querySelector('video');
      if (video) {
        setIsVideoPaused(video.paused);

        video.addEventListener('play', () => {
          setIsVideoPaused(video.paused);
        });

        return () => {
          video.removeEventListener('play', () => {
            setIsVideoPaused(video.paused);
          });
        };
      }
    }
  }, [mediaRef.current]);

  const headingType = (heading.variant.slice(0, -1) === 'order' && 'headline') || 'display';
  const headingOrder = heading.variant.at(-1);
  const headingWeight = (heading.embolden && 'bold') || 'normal';
  const subHeadingType = (subHeading.variant.slice(0, -1) === 'order' && 'headline') || 'display';
  const subHeadingOrder = subHeading.variant.at(-1);
  const subHeadingWeight = (subHeading.embolden && 'bold') || 'normal';

  const CTAbutton =
    CTA.variant === 'text' ? TextButtonWithScrollToElement : ButtonWithScrollToElement;

  const CTAprops =
    CTA.variant === 'text'
      ? {
          variant: 'standalone',
          icon: 'forward',
          iconPosition: 'trailing',
        }
      : {
          variant: CTA.variant,
        };

  useEffect(() => {
    if (ctaRef && ctaRef.current && CTA.variant === 'text') {
      const cta = ctaRef.current.querySelector('a');
      cta.setAttribute('aria-label', CTA.ariaLabel);
    }
  }, [ctaRef.current]);

  useEffect(() => {
    if (contentRef && contentRef.current) {
      const videoCta = contentRef.current.querySelector('button');

      if (videoCta && linkRef && linkRef.current) {
        const link = linkRef.current;
        link.addEventListener('click', (event) => {
          if (event.target.tagName === 'BUTTON') event.preventDefault();
        });
        return () => {
          link.removeEventListener('click', (event) => {
            if (event.target.tagName === 'BUTTON') event.preventDefault();
          });
        };
      }
    }
  }, [contentRef.current, linkRef.current]);

  return (
    <AnchorWithScroll
      linkEnabled={!CTA.linkType || CTA.linkType === 'fullTile'}
      href={CTA.href}
      newWindow={CTA.openIn}
      data-testid="content-tile-image-anchor"
      onClick={onClickEvent}
      ariaLabel={CTA.ariaLabel ? CTA.ariaLabel : CTA.label}
      linkRef={linkRef}
      {...ueProps}
    >
      <ThemeProvider theme={{ name: theme }}>
        <Container
          data-testid="content-tile-container"
          index={index}
          size={size}
          layoutName={layoutName}
          mobileBreakpointSizes={mobileBreakpointSizes}
          zoomEnabled={media.zoomEnabled}
          {...ueProps}
          ref={contentRef}
        >
          <InnerWrapper
            data-testid="content-tile-inner-wrapper"
            verticalAlignment={verticalAlignment}
            verticalAlignmentSmall={verticalAlignmentSmall}
            horizontalAlignment={horizontalAlignment}
            horizontalAlignmentSmall={horizontalAlignmentSmall}
          >
            <MediaContainer ref={mediaRef}>
              <UeContainer propertyPath={`tiles.${index}.mediaAsset`} label={assetContainerLabel}>
                <UeReference propertyPath={`tiles.${index}.mediaAsset`} behavior="component">
                  {(ueMediaProps) => (
                    <StyledAssetWrapper ref={assetRef} {...ueMediaProps}>
                      <UeElement
                        property={
                          mediaProperty(media.assetType, windowSize, isVideoPaused).property
                        }
                        type="media"
                        label={mediaProperty(media.assetType, windowSize, isVideoPaused).label}
                      >
                        {media.imageEnabled ? (
                          <Img
                            alt={
                              !isTest
                                ? setAssetParams(media, assetContainerLabel, isTest).image.alt
                                : ''
                            }
                            src={
                              !isTest
                                ? setAssetParams(media, assetContainerLabel, isTest).image.src
                                : ''
                            }
                            srcSet={
                              !isTest
                                ? setAssetParams(media, assetContainerLabel, isTest).image.srcSet
                                : ''
                            }
                            loading="lazy"
                            sizes="
                          (max-width: 23.375em) 290px,
                          (max-width: 47.9375em) 344px,
                          (max-width: 63.9375em) 738px,
                          (max-width: 89.9375em) 446px,
                          (min-width: 119.9375em) 915px,
                        "
                          />
                        ) : (
                          <ResponsiveMedia {...setAssetParams(media)} cover />
                        )}
                      </UeElement>
                    </StyledAssetWrapper>
                  )}
                </UeReference>
              </UeContainer>
            </MediaContainer>
            <ContentContainer {...ueProps}>
              <Anchor
                linkEnabled={CTA.linkType === 'textOnly'}
                href={CTA.href}
                newWindow={CTA.openIn}
                data-testid="content-tile-text-anchor"
                onClick={onClickEvent}
                aria-label={CTA.ariaLabel ? CTA.ariaLabel : CTA.label}
              >
                <LogoContainer
                  horizontalAlignment={horizontalAlignment}
                  horizontalAlignmentSmall={horizontalAlignmentSmall}
                >
                  {logo.src && <Logo src={logo.src} alt={logo.alt} heights={logo.heights} />}
                  {!logo.src && icon && <StyledIcon theme={theme} name={icon} size="l" />}
                </LogoContainer>
                {heading.text && (
                  <Heading
                    horizontalAlignment={horizontalAlignment}
                    horizontalAlignmentSmall={horizontalAlignmentSmall}
                  >
                    <Headline
                      tag={heading.htmlTag}
                      variant={{ order: headingOrder, type: headingType, weight: headingWeight }}
                    >
                      <DisclaimerWrapper onClick={stopPropagation}>
                        <UeElement type="text" property="heading_text" label="Heading">
                          {renderTextWithFootnotesReferences(heading.text)}
                        </UeElement>
                      </DisclaimerWrapper>
                    </Headline>
                  </Heading>
                )}
                {subHeading.text && !logo.src && !icon && (
                  <SubHeading
                    horizontalAlignment={horizontalAlignment}
                    horizontalAlignmentSmall={horizontalAlignmentSmall}
                  >
                    <Headline
                      tag={subHeading.htmlTag}
                      variant={{
                        order: subHeadingOrder,
                        type: subHeadingType,
                        weight: subHeadingWeight,
                      }}
                    >
                      <DisclaimerWrapper onClick={stopPropagation}>
                        <UeElement type="text" property="subHeading_text" label="Subheading">
                          {renderTextWithFootnotesReferences(subHeading.text)}
                        </UeElement>
                      </DisclaimerWrapper>
                    </Headline>
                  </SubHeading>
                )}
              </Anchor>
              {CTA.text && (
                <CTAsContainer
                  horizontalAlignment={horizontalAlignment}
                  horizontalAlignmentSmall={horizontalAlignmentSmall}
                  ref={ctaRef}
                >
                  <CTAbutton
                    key={uuid()}
                    href={CTA.href}
                    newWindow={CTA.newWindow}
                    data-testid="content-tile-cta"
                    onClick={onClickEvent}
                    size="md"
                    target={CTA.openIn === 'New tab' ? '_blank' : '_self'}
                    {...CTAprops}
                    aria-label={CTA.ariaLabel ? CTA.ariaLabel : CTA.label}
                  >
                    <UeElement type="text" property="CTA_text" label="CTA">
                      {CTA.text}
                    </UeElement>
                  </CTAbutton>
                </CTAsContainer>
              )}
            </ContentContainer>
          </InnerWrapper>
        </Container>
        <WLTPComponent theme={theme} materialDisclaimer={disclaimers} wltpTextValues={wltpKeys} />
      </ThemeProvider>
    </AnchorWithScroll>
  );
}

ContentTile.displayName = 'ContentTile';

const srcShape = shape({
  src: string.isRequired,
  poster: string.isRequired,
});

ContentTile.propTypes = {
  size: number, // Value to be applied to the height of the tile
  mobileBreakpointSizes: object, // Values to be applied to the height of the tile at 375 & 768 breakpoints
  theme: oneOf(['light', 'dark']).isRequired,
  linkType: oneOf(['fullTile', 'textOnly', 'noLink']),
  icon: string,
  media: shape({
    newWindow: bool,
    fadeEnabled: bool,
    fade: shape({
      color: string, // hex color, if none present will use theme color
      opacity: string, // 0 -1
      height: string, // percentage 0-100
      direction: oneOf(['top', 'bottom', 'left', 'right']),
    }),
    imageEnabled: bool.isRequired,
    videoEnabled: bool.isRequired,
    image: shape({
      alt: string,
      srcSet: arrayOf(
        shape({
          bp: oneOf(['xs', 's', 'm', 'l', 'xl', 'xxl']),
          src: string,
        }),
      ),
    }),
    video: shape({
      autoplay: bool,
      loop: bool,
      srcs: shape({
        xs: srcShape,
        s: srcShape,
        m: srcShape,
        l: srcShape,
        xl: srcShape,
        xxl: srcShape,
      }),
    }),
  }).isRequired,
  logo: shape({
    alt: string,
    src: string,
  }),
  heading: shape({
    text: string,
    variant: oneOf(['order1', 'order2', 'order3', 'order4', 'display1', 'display2']),
    htmlTag: oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
    embolden: bool,
  }),
  subHeading: shape({
    text: string,
    variant: oneOf(['order1', 'order2', 'order3', 'order4', 'display1', 'display2']),
    htmlTag: oneOf(['h2', 'h3', 'h4', 'h5', 'h6', 'p']),
    embolden: bool,
  }),
  CTA: shape({
    href: string,
    variant: oneOf(['primary', 'secondary', 'text']),
    text: string,
    newWindow: bool,
  }),
  horizontalAlignment: oneOf(['left', 'center', 'right']),
  horizontalAlignmentSmall: oneOf(['left', 'center', 'right']),
  verticalAlignment: oneOf(['top', 'center', 'bottom']),
  verticalAlignmentSmall: oneOf(['top', 'center', 'bottom']),
  wltpKeys: array,
  disclaimers: array,
  onClickEvent: func,
};
